import Chart from 'highcharts-react-official';
import noData from 'highcharts/modules/no-data-to-display';
import brokenAxis from 'highcharts/modules/broken-axis';

import Highcharts from 'highcharts';
import { memo, useEffect, useMemo, useRef, useCallback } from 'react';
import moment from 'moment';
import {
  AggregatedProductionVolumes,
  SlotCategory,
  MeasurementType,
  ProductionVolumeEntry,
  FreeCashFlowAggregationPeriod,
  ProductionType,
} from 'types/evaluation';
import { parseMomentUtc, US_DATE_FORMAT } from 'utils/dates';
import { partition, capitalize, escape as escapeHtml } from 'lodash-es';
import { useResolveDateRange } from 'pages/packages/show/evaluation-result/aggregated-free-cash-flow/hooks';
// Deep imports (not the barrel) so this chart doesn't drag in the SciChart exporter /
// html-to-image / the options drawer — it only needs the Highcharts exporter + presets.
import { createHighchartsExporter } from 'components/chart-export/highcharts-exporter';
import { GENERIC_CHART_ELEMENTS } from 'components/chart-export/chart-element-presets';
import type { ChartImageExporter } from 'components/chart-export/types';
import { PLOT_LINE_LABEL_ALLOW_OVERLAP } from 'components/charts/plot-line-label';
import { createYAxisFormatter } from './numberFormatter';
import { getTickOffset } from './utils';

type Props = {
  packageId: number;
  isLoading: boolean;
  aggregatedProductionVolumes: AggregatedProductionVolumes;
  slotCategoryVisibility: Record<SlotCategory, boolean>;
  measurementType: MeasurementType;
  productionTypes: readonly string[];
  effectiveDate: string;
  /**
   * Opt-in image-export seam: called with a ready-to-use image exporter for this chart (and
   * `null` on unmount). Absent by default, so the legacy aggregated-free-cash-flow consumer
   * is untouched; eval3-new passes a callback that registers the chart for content export
   * (captured offscreen from its options, so it exports even while the section is hidden).
   */
  onExporterReady?: (exporter: ChartImageExporter | null) => void;
  /**
   * Opt-in date-range override: the resolved `{ minDate, maxDate }` window. Absent by
   * default, so the legacy consumer keeps deriving the window from the per-package
   * `useResolveDateRange` hook; eval3-new passes its own resolved range (from the display
   * settings) so the chart doesn't depend on the legacy per-package atoms.
   */
  resolvedDateRange?: { minDate: moment.Moment; maxDate: moment.Moment };
};

noData(Highcharts);
brokenAxis(Highcharts);

const colors = {
  oil: 'rgb(58, 182, 90)',
  gas: 'rgb(249, 68, 76)',
  ngl: 'rgb(255, 159, 121)',
  water: 'rgb(57, 124, 255)',
  cumulativeOil: 'rgb(58, 182, 90)',
  cumulativeGas: 'rgb(249, 68, 76)',
  cumulativeNgl: 'rgb(255, 159, 121)',
  cumulativeWater: 'rgb(57, 124, 255)',
};

const UNIT_MCF = 'MCF';
const UNIT_BBL = 'BBL';

const xAxisOffset = {
  year: {
    min: 180 * 24 * 3600 * 1000,
    max: 179 * 24 * 3600 * 1000,
    unit: 'milliseconds',
  },
  month: {
    min: 14 * 24 * 3600 * 1000,
    max: 13 * 24 * 3600 * 1000,
    unit: 'milliseconds',
  },
  quarter: {
    min: 45 * 24 * 3600 * 1000,
    max: 44 * 24 * 3600 * 1000,
    unit: 'milliseconds',
  },
} as const;

export const AggregatedProductionVolumesChart = memo((prop: Props) => {
  const chartRef = useRef<{ chart: Highcharts.Chart }>(null);

  const momentInterval = prop.aggregatedProductionVolumes.interval;

  const effectiveDateUTCMoment = parseMomentUtc(prop.effectiveDate);

  const isProductionVolumeMidInterval = (
    productionVolume: ProductionVolumeEntry
  ) =>
    parseMomentUtc(productionVolume.date).isSame(
      parseMomentUtc(productionVolume.date).startOf(momentInterval)
    );

  const [[aggregatedProductionVolumes, midIntervalVolumes], interval] = [
    partition(
      prop.aggregatedProductionVolumes.productionVolumes,
      (productionVolume) => isProductionVolumeMidInterval(productionVolume)
    ),
    prop.aggregatedProductionVolumes.interval,
  ];

  const isMidIntervalNeeded = !effectiveDateUTCMoment.isSame(
    effectiveDateUTCMoment.clone().startOf(momentInterval)
  );

  const midIntervalUTCMoment = effectiveDateUTCMoment
    .clone()
    .startOf(momentInterval)
    .add(1, 'second');

  // The hook must be called unconditionally (React hook rules) — it computes the legacy
  // per-package fallback window. When the consumer injects `resolvedDateRange` (eval3-new),
  // prefer it; otherwise keep the legacy behavior (the fallback), byte-for-byte.
  const fallbackRange = useResolveDateRange(
    prop.packageId,
    prop.effectiveDate,
    interval,
    isMidIntervalNeeded
  );
  const { minDate: startDate, maxDate: endDate } =
    prop.resolvedDateRange ?? fallbackRange;

  // Build an image exporter for this chart ONLY when a consumer opts in — the legacy
  // aggregated-free-cash-flow consumer passes no callback, so this stays inert there. The
  // chart's on-screen legend is the native Highcharts legend (unlike the rescat chart's
  // React legend), so the exporter needs no `legendItems` / `filterSeries` — the offscreen
  // capture serializes the same series + legend that render on screen.
  const { onExporterReady } = prop;
  const exporter = useMemo<ChartImageExporter | null>(() => {
    if (!onExporterReady) {
      return null;
    }
    return createHighchartsExporter({
      getChart: () => chartRef.current?.chart ?? null,
      elementHooks: { getElements: () => GENERIC_CHART_ELEMENTS },
    });
  }, [onExporterReady]);

  // Push the exporter to the consumer on ready, and null it on unmount (opt-in seam).
  useEffect(() => {
    onExporterReady?.(exporter);
    return () => onExporterReady?.(null);
  }, [onExporterReady, exporter]);

  // x axis granularity
  const xAxisGranularity: Record<FreeCashFlowAggregationPeriod, any> = {
    year: {
      tickInterval: 365 * 24 * 3600 * 1000,
      dateTimeLabelFormats: {
        year: '%Y',
      },
    },
    month: {
      tickInterval: 30 * 24 * 3600 * 1000,
      dateTimeLabelFormats: {
        month: '%b',
      },
    },
    quarter: {
      tickInterval: 3 * 30 * 24 * 3600 * 1000,
      dateTimeLabelFormats: {
        month: '%b',
      },
    },
  };

  // x axis label formatter
  const xAxisLabelFormatter = useCallback(
    (value: number) => {
      if (!isFinite(value) || value <= 0 || value > Number.MAX_SAFE_INTEGER) {
        return '';
      }
      try {
        if (interval === 'year') return Highcharts.dateFormat('%Y', value);
        if (interval === 'quarter') {
          const date = parseMomentUtc(value);
          const quarter = Math.floor(date.month() / 3) + 1;
          return `Q${quarter}`;
        }
        if (interval === 'month') return Highcharts.dateFormat('%b', value);
      } catch {
        return '';
      }
      return '';
    },
    [interval]
  );

  // tooltip content renderer
  const tooltipContentRenderer = useCallback(
    (
      color: string,
      name: string,
      value?: number,
      mineralType?: string,
      isDashed?: boolean
    ) => {
      if (name.toLowerCase().includes('gas')) {
        mineralType = UNIT_MCF;
      } else if (
        name.toLowerCase().includes('oil') ||
        name.toLowerCase().includes('ngl') ||
        name.toLowerCase().includes('water')
      ) {
        mineralType = UNIT_BBL;
      }

      let displayName = escapeHtml(name);
      if (isDashed && name.includes('Cumulative')) {
        displayName = escapeHtml(name.replace(' Cumulative', ''));
      }

      return `
        <span style="color:${color}">●</span> ${
          isDashed ? '<span style="border-bottom: 1px dashed"></span> ' : ''
        }${displayName}${isDashed ? ' Cumulative' : ''}: 
        <b><span style="font-weight:bold">${
          value ? Highcharts.numberFormat(value, 0, '.', ',') : '-'
        } ${mineralType || ''}</span></b><br/>
      `;
    },
    []
  );

  // update chart loading state
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    try {
      if (prop.isLoading) {
        chart.xAxis?.[0]?.update({ visible: false });
        chart.showLoading();
        // @ts-expect-error hideNoData is a valid method but not typed in Highcharts types
        chart.hideNoData?.();
      } else {
        chart.hideLoading();
        chart.xAxis?.[0]?.update({ visible: true });
        if (!aggregatedProductionVolumes.length) {
          // @ts-expect-error showNoData is a valid method but not typed in Highcharts types
          chart.showNoData?.();
        }
      }
    } catch (error) {
      console.error('Error updating chart loading state:', error);
    }
  }, [prop.isLoading, aggregatedProductionVolumes]);

  // update chart data
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart || prop.isLoading) return;

    if (isMidIntervalNeeded) {
      // get volumes by type
      const getVolumeData = (
        type: string,
        isCumulative = false
      ): typeof midIntervalVolumes => {
        const prefix = isCumulative ? 'totalCumulative' : '';
        const measurementPrefix =
          prop.measurementType === 'gross' ? 'Gross' : 'Net';
        const capitalizedType = capitalize(type);
        let targetType = isCumulative
          ? `${prefix}${measurementPrefix}${capitalizedType}`
          : `${prop.measurementType}${capitalizedType}`;

        targetType = targetType.includes('Gas')
          ? `${targetType}Sold`
          : targetType;
        return midIntervalVolumes.filter(
          (v) => v.type === targetType && v.date
        );
      };

      const baseTypes = ['oil', 'gas', 'ngl', 'water'];

      const volumesByType = baseTypes.reduce(
        (acc, type) => {
          acc[type] = getVolumeData(type, false);
          acc[`cumulative${capitalize(type)}`] = getVolumeData(type, true);
          return acc;
        },
        {} as Record<string, typeof midIntervalVolumes>
      );

      // Add mid-interval point to each series
      ['oil', 'ngl', 'gas', 'water'].forEach((type) => {
        const series = chart.series.find(
          (s) =>
            s.name ===
            `${type === 'ngl' ? 'NGL' : capitalize(type)} ${
              type === 'gas' ? '(MCF)' : '(BBL)'
            }`
        );
        if (series) {
          series.addPoint(
            [midIntervalUTCMoment.valueOf(), volumesByType[type][0]?.volume],
            false
          );
        }
      });
      // take the volumes of cumulative oil, ngl, gas, water individual volumes
      const volumeMap = midIntervalVolumes.reduce(
        (acc, item) => {
          const measurementPrefix =
            prop.measurementType === 'gross' ? 'Gross' : 'Net';
          if (item?.type === `totalCumulative${measurementPrefix}Oil`) {
            acc.cumulativeOil = item.volume;
          } else if (item?.type === `totalCumulative${measurementPrefix}Ngl`) {
            acc.cumulativeNgl = item.volume;
          } else if (
            item?.type === `totalCumulative${measurementPrefix}GasSold`
          ) {
            acc.cumulativeGas = item.volume;
          } else if (
            item?.type === `totalCumulative${measurementPrefix}Water`
          ) {
            acc.cumulativeWater = item.volume;
          }
          return acc;
        },
        {} as Record<string, number>
      );

      const cumulativeOil = volumeMap.cumulativeOil;
      const cumulativeNgl = volumeMap.cumulativeNgl;
      const cumulativeGas = volumeMap.cumulativeGas;
      const cumulativeWater = volumeMap.cumulativeWater;

      // Add mid-interval point to cumulative series
      [
        'cumulativeOil',
        'cumulativeNgl',
        'cumulativeGas',
        'cumulativeWater',
      ].forEach((type) => {
        const baseType = type.replace('cumulative', '').toLowerCase();
        const series = chart.series.find(
          (s) =>
            s.name ===
            `${baseType === 'ngl' ? 'NGL' : capitalize(baseType)} (${
              baseType === 'gas' ? 'MCF' : 'BBL'
            }) Cumulative`
        );
        if (series) {
          let value;
          if (type === 'cumulativeOil') value = cumulativeOil;
          else if (type === 'cumulativeNgl') value = cumulativeNgl;
          else if (type === 'cumulativeGas') value = cumulativeGas;
          else if (type === 'cumulativeWater') value = cumulativeWater;

          series.addPoint([midIntervalUTCMoment.valueOf(), value], false);
        }
      });

      // update x axis
      chart.xAxis[0].update(
        {
          labels: {
            formatter: function (
              this: Highcharts.AxisLabelsFormatterContextObject
            ) {
              return xAxisLabelFormatter(Number(this.value));
            },
          },
          tickPositioner: function () {
            const positions = this.tickPositions || [];
            if (!positions.includes(midIntervalUTCMoment.valueOf())) {
              positions.push(midIntervalUTCMoment.valueOf());
              positions.sort((a, b) => a - b);
            }
            return positions;
          },
        },
        false
      );
      chart.redraw();
    }

    // add plot line
    chart.xAxis[0].addPlotLine({
      value: midIntervalUTCMoment.clone().add(2, 'seconds').valueOf(),
      id: 'mid-interval-plot-line',
      color: '#FFD000',
      width: 1.5,
      zIndex: 1,
      label: {
        ...PLOT_LINE_LABEL_ALLOW_OVERLAP,
        text: `Eff Date ${effectiveDateUTCMoment.format(US_DATE_FORMAT)}`,
        align: 'center',
        verticalAlign: 'top',
        y: -10,
        rotation: 0,
      },
    });

    const tickOffset = getTickOffset(
      chart.xAxis[0],
      chart.plotWidth,
      isMidIntervalNeeded,
      xAxisOffset[interval]
    );

    // @ts-expect-error - Highcharts type definitions are missing plotLinesAndBands property
    const plotLines = chart.xAxis[0].plotLinesAndBands;
    const midIntervalPlotLine = plotLines.find(
      (line: any) => line.id === 'mid-interval-plot-line'
    );
    if (midIntervalPlotLine && isFinite(tickOffset)) {
      midIntervalPlotLine?.svgElem?.attr({
        transform: `translate(-${tickOffset}, 0)`,
        stroke: '#FFD000',
      });
    }

    chart.redraw();

    // cleanup - remove plot line and series
    return () => {
      // React 18 can run this cleanup after HighchartsReact has already
      // destroyed the chart on unmount; touching a destroyed chart throws
      // (reads this.legend of undefined). Bail if the chart is gone.
      if (!chart?.renderer) return;
      chart.xAxis?.[0]?.removePlotLine('mid-interval-plot-line');
      [
        'oil',
        'ngl',
        'gas',
        'water',
        'cumulativeOil',
        'cumulativeNgl',
        'cumulativeGas',
        'cumulativeWater',
      ].forEach((type) => {
        const series = chart?.series?.find(
          (s) => s.options.id === `mid-interval-series-${type}`
        );
        series?.remove(false);
      });
    };
  });

  // get data
  const getData = () => {
    if (!aggregatedProductionVolumes?.length) return [];

    const measurementPrefix =
      prop.measurementType === 'gross' ? 'Gross' : 'Net';
    const baseTypes = ['oil', 'gas', 'ngl', 'water'] as const;

    const getVolumeKey = (type: string, isCumulative = false): string => {
      const capitalized = capitalize(type);
      if (isCumulative) {
        return type === 'gas'
          ? `totalCumulative${measurementPrefix}GasSold`
          : `totalCumulative${measurementPrefix}${capitalized}`;
      } else if (type === 'gas' && !isCumulative) {
        return `${prop.measurementType}GasSold`;
      }
      return `${prop.measurementType}${capitalized}`;
    };

    // Group data by date to handle cumulative values properly
    const groupedByDate = aggregatedProductionVolumes.reduce(
      (acc, item) => {
        if (!acc[item.date]) {
          acc[item.date] = [];
        }
        acc[item.date].push(item);
        return acc;
      },
      {} as Record<string, typeof aggregatedProductionVolumes>
    );

    // Keep track of last known values for each cumulative type
    const lastKnownCumulativeValues: Record<string, number> = baseTypes.reduce(
      (acc, type) => {
        const cumulativeKey = getVolumeKey(type, true);
        acc[cumulativeKey] = 0;
        return acc;
      },
      {} as Record<string, number>
    );

    // Process data by date to maintain cumulative values
    const processedData: typeof aggregatedProductionVolumes = [];

    // Sort by dates
    const sortedDates = Object.keys(groupedByDate).sort(
      (a, b) => parseMomentUtc(a).valueOf() - parseMomentUtc(b).valueOf()
    );

    sortedDates.forEach((dateString) => {
      const items = groupedByDate[dateString];

      // Regular non-cumulative data remains the same
      const regularItems = items.filter((item) => {
        return item.type && !item.type.toString().startsWith('totalCumulative');
      });
      processedData.push(...regularItems);

      // Process cumulative data - update last known values and include existing items
      const cumulativeItems = items.filter((item) => {
        return item.type && item.type.toString().startsWith('totalCumulative');
      });

      // For each cumulative item, ensure it's never lower than the last known value
      const updatedCumulativeItems = cumulativeItems.map((item) => {
        if (item.type) {
          const lastKnownValue =
            lastKnownCumulativeValues[item.type.toString()] || 0;
          // If current value is less than last known value, use the last known value
          if (item.volume < lastKnownValue) {
            return {
              ...item,
              volume: lastKnownValue,
            };
          }
          // Update last known value with current value (since it's higher)
          lastKnownCumulativeValues[item.type.toString()] = item.volume;
        }
        return item;
      });

      // Add updated cumulative items to processed data
      processedData.push(...updatedCumulativeItems);

      // For each cumulative type, add missing ones with last known values
      baseTypes.forEach((type) => {
        const cumulativeKey = getVolumeKey(type, true);
        const existingItem = items.find((item) => item.type === cumulativeKey);

        if (!existingItem) {
          // Determine the cumulative category based on type
          let cumulativeCategory: ProductionType;
          if (type === 'oil') cumulativeCategory = 'cumulativeOil';
          else if (type === 'gas') cumulativeCategory = 'cumulativeGas';
          else if (type === 'ngl') cumulativeCategory = 'cumulativeNgl';
          else if (type === 'water') cumulativeCategory = 'cumulativeWater';
          else return;

          // If no data for this date/type, use last known value
          processedData.push({
            date: dateString,
            category: cumulativeCategory,
            type: cumulativeKey as any,
            volume: lastKnownCumulativeValues[cumulativeKey] || 0,
          });
        }
      });
    });

    // Convert processedData to volumesByType
    const volumesByType = baseTypes.reduce(
      (acc, type) => {
        acc[type] = processedData.filter((v) => v.type === getVolumeKey(type));
        acc[`cumulative${capitalize(type)}`] = processedData.filter(
          (v) => v.type === getVolumeKey(type, true)
        );
        return acc;
      },
      {} as Record<string, typeof processedData>
    );

    const combinedResults: any[] = [];

    // create series
    baseTypes.forEach((type, index) => {
      if (!prop.productionTypes.includes(type)) return;

      const unit = type === 'gas' ? UNIT_MCF : UNIT_BBL;
      const label = type === 'ngl' ? 'NGL' : capitalize(type);
      const cumulativeKey = `cumulative${capitalize(type)}`;

      // get cumulative data points and update 0 values to null to support tooltip
      const cumulativeData = (volumesByType[cumulativeKey] || []).map((v) => {
        const timestamp = parseMomentUtc(v.date).valueOf();
        return v.volume === 0 ? [timestamp, null] : [timestamp, v.volume];
      });

      // get regular data points and update 0 values to null to support tooltip
      const regularData = (volumesByType[type] || []).map((v) => {
        const timestamp = parseMomentUtc(v.date).valueOf();
        return v.volume === 0 ? [timestamp, null] : [timestamp, v.volume];
      });

      const createSeries = (
        id: string,
        name: string,
        dataKey: string,
        isCumulative = false
      ) => {
        return {
          id,
          name: name,
          mineralType: unit,
          color: colors[dataKey as keyof typeof colors],
          data: isCumulative ? cumulativeData : regularData,
          yAxis: 0,
          type: 'spline',
          lineWidth: 2,
          dashStyle: isCumulative ? 'Dash' : undefined,
          visible: true,
          showInLegend: true,
          pointPlacement: 'on',
          legendIndex: isCumulative ? baseTypes.length + index : index,
          linkedTo: isCumulative ? `:series-${type}` : undefined,
          enableMouseTracking: true,
        };
      };

      combinedResults.push(
        createSeries(`series-${type}`, `${label} (${unit})`, type, false)
      );

      combinedResults.push(
        createSeries(
          `series-${cumulativeKey}`,
          `${label} (${unit}) Cumulative`,
          cumulativeKey,
          true
        )
      );
    });

    // return series
    return [...combinedResults];
  };

  return (
    <Chart
      ref={chartRef}
      highcharts={Highcharts}
      options={{
        credits: {
          enabled: false,
        },
        // Importing the Highcharts image-exporter (for the opt-in content-export seam)
        // registers the `exporting` module, which otherwise renders its default context
        // (hamburger) button. Disable that button so this chart looks exactly as before on
        // every consumer (legacy + eval3-new) — the export API stays available (offscreen
        // capture uses it directly, not this button).
        exporting: {
          buttons: {
            contextButton: {
              enabled: false,
            },
          },
        },
        chart: {
          type: 'spline',
          zoomType: 'x',
          overflow: 'visible',
          marginRight: 100,
          marginLeft: 100,
        },
        title: {
          text: 'Production Plot',
          align: 'center',
          y: 5,
        },
        xAxis: [
          {
            min: startDate
              .subtract(xAxisOffset[interval].min, xAxisOffset[interval].unit)
              .valueOf(),
            max: endDate
              .startOf(interval)
              .add(1, 'day')
              .add(xAxisOffset[interval].max, xAxisOffset[interval].unit)
              .valueOf(),
            type: 'datetime',
            tickLength: 0,
            tickInterval: xAxisGranularity[interval]?.tickInterval,
            labels: {
              useHTML: true,
              formatter: function (
                this: Highcharts.AxisLabelsFormatterContextObject
              ) {
                return xAxisLabelFormatter(Number(this.value));
              },
              y: 15,
            },
            ordinal: false,

            breaks: isMidIntervalNeeded
              ? [
                  {
                    from: midIntervalUTCMoment.valueOf(),
                    to: midIntervalUTCMoment.valueOf(),
                    breakSize:
                      interval === 'year'
                        ? 365 * 24 * 3600 * 1000
                        : 91.25 * 24 * 3600 * 1000,
                  },
                ]
              : [],
          },
          {
            type: 'datetime',
            tickLength: 5,
            tickWidth: 1,
            opposite: false,
            linkedTo: 0,
            visible: interval !== 'year',
            labels: {
              useHTML: true,
              formatter: function (
                this: Highcharts.AxisLabelsFormatterContextObject
              ) {
                return Highcharts.dateFormat('%Y', Number(this.value));
              },
            },
            tickPositioner: function (
              this: Highcharts.AxisLabelsFormatterContextObject
            ) {
              const chart = this.chart;
              const xMin =
                chart.xAxis[0].min || chart.xAxis[0].getExtremes().min || 0;
              const xMax =
                chart.xAxis[0].max || chart.xAxis[0].getExtremes().max || 0;
              const startYear = new Date(xMin).getFullYear();
              const endYear = new Date(xMax).getFullYear();

              const positions = [];
              for (let year = startYear; year <= endYear; year++) {
                positions.push(Date.UTC(year, 0, 1));
              }
              return positions;
            },
          },
        ],
        yAxis: {
          title: {
            text: 'BBL & MCF',
            style: {
              fontSize: '12px',
              fontWeight: 'bold',
            },
          },
          type: 'logarithmic',
          min: 0.1,
          labels: {
            useHTML: true,
            formatter: createYAxisFormatter({
              decimals: 0,
              includeDollarSign: false,
            }),
          },
        },

        legend: {
          useHTML: true,
          labelFormatter: function (this: Highcharts.Point): string {
            try {
              const baseWidth = '200px';

              const isCumulative =
                this.name && this.name.includes('Cumulative');
              const displayName = escapeHtml(this.name || '');

              if (isCumulative) {
                const baseName = displayName.replace(' Cumulative', '');
                return (
                  '<div style="display: flex; align-items: center;">' +
                  '<div style="width: ' +
                  baseWidth +
                  ';">' +
                  baseName +
                  '<span style="font-weight: normal"> Cumulative</span></div>' +
                  '</div>'
                );
              } else {
                return (
                  '<div style="display: flex; align-items: center;">' +
                  '<div style="width: ' +
                  baseWidth +
                  ';">' +
                  displayName +
                  '</div>' +
                  '</div>'
                );
              }
            } catch (error) {
              console.error('Error in legend formatter:', error, this.name);
              return this.name || '';
            }
          },
          itemStyle: {
            lineHeight: '1.5em',
          },
          symbolWidth: 20,
          margin: 10,
          itemDistance: 40,
          layout: 'horizontal',
          align: 'center',
          verticalAlign: 'bottom',
          itemMarginTop: 5,
          itemMarginBottom: 5,
        },
        series: getData(),
        plotOptions: {
          spline: {
            marker: { enabled: false },
            connectNulls: true,
          },
        },
        tooltip: {
          shared: true,
          crosshairs: true,
          useHTML: true,
          snap: 10,
          formatter: function (this: Highcharts.TooltipFormatterContextObject) {
            let html = `<span style="font-weight:bold">${xAxisLabelFormatter(
              Number(this.x)
            )} </span><span style="font-weight:bold">${
              interval !== 'year'
                ? Highcharts.dateFormat('%Y', Number(this.x))
                : ''
            }</span><br/>`;

            const pointsByType: Record<
              string,
              { regular?: any; cumulative?: any }
            > = {};

            this.points?.forEach((point) => {
              try {
                const seriesName = point.series.name;
                const isCumulative = seriesName
                  .toLowerCase()
                  .includes('cumulative');

                let typeKey = '';
                if (seriesName.toLowerCase().includes('oil')) {
                  typeKey = 'oil';
                } else if (seriesName.toLowerCase().includes('gas')) {
                  typeKey = 'gas';
                } else if (seriesName.toLowerCase().includes('ngl')) {
                  typeKey = 'ngl';
                } else if (seriesName.toLowerCase().includes('water')) {
                  typeKey = 'water';
                } else {
                  typeKey = seriesName.toLowerCase();
                }

                if (!pointsByType[typeKey]) {
                  pointsByType[typeKey] = {};
                }

                if (isCumulative) {
                  pointsByType[typeKey].cumulative = point;
                } else {
                  pointsByType[typeKey].regular = point;
                }
              } catch (error) {
                console.error('Error processing tooltip point:', error);
              }
            });

            Object.keys(pointsByType).forEach((type) => {
              const points = pointsByType[type];

              if (points.regular) {
                const point = points.regular;
                const mineralType = type === 'gas' ? UNIT_MCF : UNIT_BBL;
                html += tooltipContentRenderer(
                  point.color as string,
                  point.series.name,
                  point.y ?? undefined,
                  mineralType,
                  false
                );
              }

              if (points.cumulative) {
                const point = points.cumulative;
                const mineralType = type === 'gas' ? UNIT_MCF : UNIT_BBL;
                html += tooltipContentRenderer(
                  point.color as string,
                  point.series.name,
                  point.y ?? undefined,
                  mineralType,
                  true
                );
              }
            });

            return html;
          },
        },
        noData: {
          style: {
            fontSize: '16px',
          },
        },
      }}
    />
  );
});
AggregatedProductionVolumesChart.displayName =
  'AggregatedProductionVolumesChart';
